export type RfpProjectStatus = 'draft' | 'processing' | 'complete' | 'error'
export type RfpStage = 'pre_questions' | 'questions_submitted' | 'response_drafting' | 'submitted' | 'under_review' | 'preferred' | 'closed_won' | 'closed_lost'
export type RfpFit = 'tbd' | 'strong' | 'weak' | 'neutral' | 'no_fit'
export type RfpConfidence = 'high' | 'medium' | 'low' | null
export type RfpSource = 'pending' | 'reference' | 'cassidy' | 'cassidy_no_answer' | 'manual'
export type RfpRequirementStatus = 'pending' | 'ai_draft' | 'assigned' | 'auto_accepted' | 'approved' | 'flagged'

export interface ColumnMapping {
  requirement: string    // column header for the requirement description
  level?: string         // column header for requirement level (mandatory/optional)
  section?: string       // column header for section/category grouping
  name?: string          // column header for requirement name/sub-heading within a section
  response?: string      // column header for existing/expected response
}

export type QualYesNo = 'yes' | 'no' | null
export type PresalesRecommendation = 'yes' | 'no' | 'maybe' | null

export interface QualificationChecklist {
  high_volume: QualYesNo
  multi_product: QualYesNo
  multi_country: QualYesNo
  target_segment: QualYesNo
  products: string[]
  competitors: string
  current_vendor: string
  current_solution: string
  pain_points: string
  reason_for_change: string
  presales_recommendation: PresalesRecommendation
}

export const RFP_PRODUCTS = [
  { key: 'kyc', label: 'KYC' },
  { key: 'aml', label: 'AML' },
  { key: 'idv', label: 'IDV' },
  { key: 'kyb', label: 'KYB' },
  { key: 'transaction_monitoring', label: 'Transaction Monitoring' },
  { key: 'case_management', label: 'Case Management' },
  { key: 'fraud', label: 'Fraud' },
  { key: 'orchestration', label: 'Orchestration' },
  { key: 'international_kyc', label: 'International KYC' },
  { key: 'international_kyb', label: 'International KYB' },
  { key: 'risk', label: 'Risk' },
  { key: 'biometrics', label: 'Biometrics' },
] as const

export const QUALIFICATION_CRITERIA: Array<{ key: 'high_volume' | 'multi_product' | 'multi_country' | 'target_segment'; label: string }> = [
  { key: 'high_volume', label: 'Is the opportunity high volume?' },
  { key: 'multi_product', label: 'Does it extend across multiple products (KYC, AML, IDV, KYB)?' },
  { key: 'multi_country', label: 'Does it extend across multiple countries?' },
  { key: 'target_segment', label: 'Is it a target industry segment?' },
]

export const EMPTY_CHECKLIST: QualificationChecklist = {
  high_volume: null,
  multi_product: null,
  multi_country: null,
  target_segment: null,
  products: [],
  competitors: '',
  current_vendor: '',
  current_solution: '',
  pain_points: '',
  reason_for_change: '',
  presales_recommendation: null,
}

export const YES_NO_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
  yes: { label: 'Yes', color: 'text-green-400', bg: 'bg-green-500/10 border-green-500/20' },
  no: { label: 'No', color: 'text-red-400', bg: 'bg-red-500/10 border-red-500/20' },
}

export const RECOMMENDATION_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
  yes: { label: 'Yes', color: 'text-green-400', bg: 'bg-green-500/10 border-green-500/20' },
  maybe: { label: 'Maybe', color: 'text-amber-400', bg: 'bg-amber-500/10 border-amber-500/20' },
  no: { label: 'No', color: 'text-red-400', bg: 'bg-red-500/10 border-red-500/20' },
}

export interface RfpProject {
  id: number
  title: string
  deal_id: number | null
  status: RfpProjectStatus
  stage: RfpStage
  column_mapping: ColumnMapping | null
  qualification_checklist: QualificationChecklist | null
  question_cutoff_date: string | null
  response_due_date: string | null
  original_filename: string | null
  total_requirements: number
  answered_count: number
  created_by: string | null
  created_by_name: string | null
  created_at: string
  updated_at: string
  response_strategy?: string | null
  owner_id?: number | null
  presales_owner_id?: number | null
  customer_name?: string
  sheet_url?: string | null
  last_synced_at?: string | null
  slack_channel_id?: string | null
  reminders_active?: boolean
  reminders_started_at?: string | null
  reminder_images?: boolean
  brain_enabled?: boolean
  brain_review_shadow?: boolean
  generated_questions_json?: string | null
  questions_prompt?: string | null
  self_eval_enabled?: boolean
  self_eval_options?: string[] | null
  question_granularity?: boolean
  consolidate_enabled?: boolean
  company_overview?: string | null
  regulatory_environment?: string | null
  industry_context?: string | null
}

export type RfpQuestionType = 'yes_no' | 'confirm' | 'describe' | 'list' | 'quantity' | 'open'

export interface RfpRequirement {
  id: number
  project_id: number
  row_index: number
  sheet_name: string | null
  section: string | null
  requirement_name: string | null
  requirement_text: string
  requirement_level: string | null
  original_response: string | null
  ai_response: string | null
  final_response: string | null
  fit: RfpFit
  confidence: RfpConfidence
  source: RfpSource
  status: RfpRequirementStatus
  assigned_to: string | null
  assigned_to_name: string | null
  tags: string | null
  assigned_category: string | null
  notes: string | null
  gap_notes: string | null
  brain_response: string | null
  brain_review_response: string | null
  self_eval: string | null
  question_type: RfpQuestionType | null
  parent_requirement_id: number | null
  sub_question_index: number | null
  created_at: string
  updated_at: string
}

export type RfpIndexStatus = 'raw' | 'indexed' | 'tagging' | 'tagged' | 'error'

export interface RfpReferenceDoc {
  id: number
  project_id: number | null
  title: string
  original_filename: string | null
  content_text: string
  row_count: number
  qa_count: number
  index_status: RfpIndexStatus
  created_by: string | null
  created_by_name: string | null
  created_at: string
}

export interface RfpQaPair {
  id: number
  reference_id: number
  row_index: number | null
  section: string | null
  question: string
  answer: string
  tags: string | null
  tag_status: 'pending' | 'tagged' | 'error'
  created_at: string
}

export const INDEX_STATUS_CONFIG: Record<RfpIndexStatus, { label: string; color: string }> = {
  raw: { label: 'Not indexed', color: 'text-gray-400' },
  indexed: { label: 'Indexed', color: 'text-blue-400' },
  tagging: { label: 'Partially tagged', color: 'text-amber-400' },
  tagged: { label: 'Ready', color: 'text-green-400' },
  error: { label: 'Error', color: 'text-red-400' },
}

export interface RfpTeamMember {
  id: number
  name: string
  email: string
  expertise_areas: string[]
  description: string | null
  created_at: string
}

export interface RfpProjectTeamMember {
  id: number
  project_id: number
  role: string
  name: string
  email: string
  created_at: string
}

export type RfpRoutingCategory = 'kyb' | 'idv' | 'kyc' | 'aml' | 'fraud' | 'technical' | 'security' | 'commercial' | 'financial' | 'implementation'

export interface RfpDefaultRouting {
  id: number
  category: RfpRoutingCategory
  name: string
  email: string
  updated_at: string
}

export const ROUTING_CATEGORIES: Array<{ key: RfpRoutingCategory; label: string }> = [
  { key: 'kyb', label: 'KYB / Business Verification' },
  { key: 'idv', label: 'Identity Verification' },
  { key: 'kyc', label: 'KYC' },
  { key: 'aml', label: 'AML / Compliance' },
  { key: 'fraud', label: 'Fraud' },
  { key: 'technical', label: 'Technical' },
  { key: 'security', label: 'Security' },
  { key: 'commercial', label: 'Commercial / Due Diligence' },
  { key: 'financial', label: 'Financial / Corporate' },
  { key: 'implementation', label: 'Implementation / Delivery' },
]

export const PROJECT_TEAM_ROLES = [
  { key: 'salesperson', label: 'Salesperson' },
  { key: 'technical_lead', label: 'Technical Lead' },
  { key: 'custom', label: 'Other' },
] as const

export const FIT_CONFIG: Record<RfpFit, { label: string; color: string; bg: string }> = {
  tbd: { label: 'TBD', color: 'text-gray-400', bg: 'bg-gray-500/10' },
  strong: { label: 'Strong Fit', color: 'text-green-400', bg: 'bg-green-500/10' },
  weak: { label: 'Weak Fit', color: 'text-amber-400', bg: 'bg-amber-500/10' },
  neutral: { label: 'Neutral', color: 'text-blue-400', bg: 'bg-blue-500/10' },
  no_fit: { label: 'No Fit', color: 'text-red-400', bg: 'bg-red-500/10' },
}

export const CONFIDENCE_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
  high: { label: 'High', color: 'text-green-400', bg: 'bg-green-500/10' },
  medium: { label: 'Medium', color: 'text-amber-400', bg: 'bg-amber-500/10' },
  low: { label: 'Low', color: 'text-orange-400', bg: 'bg-orange-500/10' },
}

export const STATUS_CONFIG: Record<RfpRequirementStatus, { label: string; color: string }> = {
  pending: { label: 'Pending', color: 'text-gray-400' },
  ai_draft: { label: 'AI Draft', color: 'text-blue-400' },
  assigned: { label: 'Assigned', color: 'text-amber-400' },
  auto_accepted: { label: 'Auto-Accepted', color: 'text-green-400' },
  approved: { label: 'Approved', color: 'text-green-400' },
  flagged: { label: 'Flagged', color: 'text-red-400' },
}

export const SELF_EVAL_PRESETS: Array<{ label: string; options: string[] }> = [
  { label: 'Full / Partial / No Match', options: ['Full Match', 'Partial Match', 'No Match'] },
  { label: 'Comply / Partially / Does Not', options: ['Comply', 'Partially Comply', 'Does Not Comply'] },
  { label: 'Yes / Partial / No', options: ['Yes', 'Partial', 'No'] },
  { label: 'Met / Partially Met / Not Met', options: ['Met', 'Partially Met', 'Not Met'] },
]

export const PROJECT_STATUS_CONFIG: Record<RfpProjectStatus, { label: string; color: string }> = {
  draft: { label: 'Draft', color: 'text-gray-400' },
  processing: { label: 'Processing...', color: 'text-blue-400' },
  complete: { label: 'Complete', color: 'text-green-400' },
  error: { label: 'Error', color: 'text-red-400' },
}

export const RFP_STAGES: Array<{ key: RfpStage; label: string; color: string; bg: string }> = [
  { key: 'pre_questions', label: 'Pre-Questions', color: 'text-gray-400', bg: 'bg-gray-500/10' },
  { key: 'questions_submitted', label: 'Questions Submitted', color: 'text-blue-400', bg: 'bg-blue-500/10' },
  { key: 'response_drafting', label: 'Drafting Response', color: 'text-indigo-400', bg: 'bg-indigo-500/10' },
  { key: 'submitted', label: 'Submitted', color: 'text-violet-400', bg: 'bg-violet-500/10' },
  { key: 'under_review', label: 'Under Review', color: 'text-amber-400', bg: 'bg-amber-500/10' },
  { key: 'preferred', label: 'Preferred Supplier', color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
  { key: 'closed_won', label: 'Closed Won', color: 'text-green-400', bg: 'bg-green-500/10' },
  { key: 'closed_lost', label: 'Closed Lost', color: 'text-red-400', bg: 'bg-red-500/10' },
]

export const STAGE_CONFIG: Record<RfpStage, { label: string; color: string; bg: string }> = Object.fromEntries(
  RFP_STAGES.map(s => [s.key, { label: s.label, color: s.color, bg: s.bg }])
) as Record<RfpStage, { label: string; color: string; bg: string }>
